import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Toolbar, useTheme } from '@mui/material';

import { inputInformationWidth } from '../InputInformation';
import PatientHandoverSection from '../InputInformation/PatientHandoverSection';
import { MAIN_SEARCH_BAR_HEIGHT, MAIN_TOP_BAR_HEIGHT } from '../MainTopBar';
import { StyledContentContainer } from '../style';
import PatientInfo from './PatientInfo';
import SearchToolbar from './SearchToolbar';
import Content from './Content';

interface Props {
  menuDrawerWidth: number;
  coachRef: any;
  coachRefA: any;
  coachRefB: any;
  isDesktopLayout: boolean;
}

const DisplayInformation = (props: Props) => {
  const { palette } = useTheme();
  const { menuDrawerWidth, coachRef, coachRefA, coachRefB, isDesktopLayout } =
    props;
  const searchBarRef = useRef<HTMLDivElement | null>(null);
  const [mobileSearchBarHeight, setMobileSearchBarHeight] = useState(
    MAIN_SEARCH_BAR_HEIGHT,
  );

  const setSearchToolbarRef = useCallback(
    (node: HTMLDivElement | null) => {
      searchBarRef.current = node;
      if (coachRef) {
        coachRef.current = node;
      }
    },
    [coachRef],
  );

  useEffect(() => {
    if (isDesktopLayout) return;
    const el = searchBarRef.current;
    if (!el) return;

    const updateHeight = () => {
      setMobileSearchBarHeight(el.getBoundingClientRect().height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isDesktopLayout]);

  const containerWidth = isDesktopLayout
    ? {
        xs:
          menuDrawerWidth !== 220
            ? `calc(100% - ${inputInformationWidth.xs}px)`
            : `calc(100% - ${menuDrawerWidth}px - ${inputInformationWidth.xs}px)`,
        xl: `calc(100% - ${menuDrawerWidth}px - ${inputInformationWidth.xl}px)`,
      }
    : '100%';

  return (
    <Box
      display="flex"
      component="main"
      flexDirection="column"
      width={containerWidth}
      flex={1}
      minWidth={0}
    >
      {!isDesktopLayout && (
        <Box
          sx={{ height: mobileSearchBarHeight, flexShrink: 0 }}
          aria-hidden
        />
      )}
      <Toolbar
        ref={setSearchToolbarRef}
        sx={{
          px: { xs: 1, sm: 2 },
          py: isDesktopLayout ? 0 : 1.5,
          boxShadow: '-4px 4px 10px rgba(0, 0, 0, 0.04)',
          backgroundColor: '#fff',
          width: '100%',
          ...(isDesktopLayout
            ? {}
            : {
                position: 'fixed',
                top: MAIN_TOP_BAR_HEIGHT,
                left: 0,
                right: 0,
                minHeight: 'unset !important',
                height: 'auto !important',
                maxHeight: 'none',
                overflow: 'visible',
                alignItems: 'stretch',
                flexDirection: 'column',
                zIndex: theme => theme.zIndex.appBar,
              }),
        }}
      >
        <Box sx={{ width: '100%' }}>
          <SearchToolbar />
        </Box>
      </Toolbar>

      <StyledContentContainer
        style={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: `${palette.primary.light}`,
        }}
      >
        <PatientInfo />
        {!isDesktopLayout && (
          <Box sx={{ mb: 2 }}>
            <PatientHandoverSection coachRef={coachRefA} />
          </Box>
        )}
        <Content coachRef={coachRefB} />
      </StyledContentContainer>
    </Box>
  );
};

export default DisplayInformation;
